import { db, isQuotaError, isUnavailableOrOfflineError } from '../firebase';
import { collection, getDocs, query, where, DocumentData, QuerySnapshot, Query, getDoc, doc } from 'firebase/firestore';
import { safeStorage } from './safeStorage';

const CACHE_PREFIX = 'school_app_cache_';
const DEFAULT_TTL = 10 * 60 * 1000; // 10 minutes (reduced from 24h for better UX)

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export const dataCache = {
  set: <T>(key: string, data: T, ttl: number = DEFAULT_TTL) => {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl
    };
    const cacheKey = CACHE_PREFIX + key;
    const serializedData = JSON.stringify(entry);

    try {
      safeStorage.setItem(cacheKey, serializedData);
    } catch (e: any) {
      // If it's a quota error, try to clear some old cache and try again
      const isStorageQuotaError = 
        e.name === 'QuotaExceededError' || 
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        (e.message && e.message.includes('quota')) ||
        (e.message && e.message.includes('setItem'));

      if (isStorageQuotaError) {
        console.warn('safeStorage quota exceeded. Attempting to clear expired cache...');
        
        // 1. Clear expired items first
        let cleared = 0;
        try {
          safeStorage.keys().forEach(k => {
            if (k.startsWith(CACHE_PREFIX)) {
              try {
                const entryVal = safeStorage.getItem(k);
                if (!entryVal) return;
                const entry: CacheEntry<any> = JSON.parse(entryVal);
                if (Date.now() - entry.timestamp > entry.ttl) {
                  safeStorage.removeItem(k);
                  cleared++;
                }
              } catch (err) {
                safeStorage.removeItem(k);
                cleared++;
              }
            }
          });
        } catch (keysErr) {
          console.error("Failed to iterate storage keys", keysErr);
        }

        // 2. If still failing, clear the oldest 20% of entries
        if (cleared === 0) {
          const entries = safeStorage.keys()
            .filter(k => k.startsWith(CACHE_PREFIX))
            .map(k => {
              try {
                const entry: CacheEntry<any> = JSON.parse(safeStorage.getItem(k) || '');
                return { key: k, timestamp: entry.timestamp };
              } catch (err) {
                return { key: k, timestamp: 0 };
              }
            })
            .sort((a, b) => a.timestamp - b.timestamp);
          
          const toRemove = Math.max(1, Math.floor(entries.length * 0.2));
          for (let i = 0; i < toRemove; i++) {
            safeStorage.removeItem(entries[i].key);
          }
          console.warn(`Cleared ${toRemove} oldest cache entries due to quota.`);
        }

        // Try setting again once more
        try {
          safeStorage.setItem(cacheKey, serializedData);
        } catch (retryError) {
          console.error('Failed to set cache even after clearing space:', retryError);
          // If it still fails, we just don't cache this item. 
          // The app should continue to function without cache.
        }
      } else {
        console.error('Failed to set cache for unknown reason:', e);
      }
    }
  },

  get: <T>(key: string, ignoreExpiry = false): T | null => {
    try {
      const raw = safeStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;

      const entry: CacheEntry<T> = JSON.parse(raw);
      const isExpired = Date.now() - entry.timestamp > entry.ttl;
      if (isExpired && !ignoreExpiry) {
        return null;
      }
      return entry.data;
    } catch (e) {
      console.warn('Cache get failed:', e);
      try {
        safeStorage.removeItem(CACHE_PREFIX + key);
      } catch (err) {}
      return null;
    }
  },

  remove: (key: string) => {
    try {
      safeStorage.removeItem(CACHE_PREFIX + key);
      delete pendingRequests[key];
    } catch (e) {}
  },

  removePrefix: (prefix: string) => {
    try {
      const fullPrefix = CACHE_PREFIX + prefix;
      safeStorage.keys()
        .filter(key => key.startsWith(fullPrefix))
        .forEach(key => safeStorage.removeItem(key));
      Object.keys(pendingRequests)
        .filter(key => key.startsWith(prefix))
        .forEach(key => delete pendingRequests[key]);
    } catch (e) {}
  },

  clear: () => {
    try {
      safeStorage.keys()
        .filter(key => key.startsWith(CACHE_PREFIX))
        .forEach(key => safeStorage.removeItem(key));
    } catch (e) {}
  }
};

export const clearDataCache = () => dataCache.clear();

// Keep track of pending requests to avoid duplicate concurrent fetches
const pendingRequests: Record<string, Promise<any>> = {};

// Helper to avoid indefinite hanging on Firestore network stalls
const withTimeout = <T>(promise: Promise<T>, timeoutMs = 8000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Firestore request timed out')), timeoutMs))
  ]);
};

/**
 * Fetches a collection with caching and request deduplication.
 */
export const getCachedCollection = async <T = DocumentData>(
  collectionName: string, 
  forceRefresh = false,
  ttl: number = DEFAULT_TTL
): Promise<T[]> => {
  if (!forceRefresh) {
    const cached = dataCache.get<T[]>(collectionName);
    if (cached) return cached;
  }

  // If there's already a pending request for this collection, return it
  if (pendingRequests[collectionName]) {
    return pendingRequests[collectionName];
  }

  const fetchPromise = (async () => {
    try {
      console.log(`Fetching collection: ${collectionName} from Firestore`);
      const snapshot = await withTimeout(getDocs(collection(db, collectionName)), 8000);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as T));
      dataCache.set(collectionName, data);
      return data;
    } catch (error: any) {
      const errMsg = error?.message || String(error);
      const isPermError = errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions');
      const isUnavailable = isUnavailableOrOfflineError(error);

      if (isQuotaError(error) || isPermError || isUnavailable || errMsg.includes('timed out')) {
        const fallback = dataCache.get<T[]>(collectionName, true);
        if (fallback) {
          console.warn(`Quota, permission, timeout or connection issue for collection ${collectionName}, using fallback data.`);
          return fallback;
        }
        if (isPermError) {
          console.warn(`Permission denied for collection ${collectionName}, returning empty array.`);
          return [];
        }
        if (isUnavailable || errMsg.includes('timed out')) {
          console.warn(`Firestore backend unavailable/timed out for collection ${collectionName}, returning empty array.`);
          return [];
        }
        console.error(`Quota exceeded for collection ${collectionName} and no fallback available.`);
      }
      throw error;
    } finally {
      delete pendingRequests[collectionName];
    }
  })();

  pendingRequests[collectionName] = fetchPromise;
  return fetchPromise;
};

/**
 * Fetches a query with caching and request deduplication.
 */
export const getCachedQuery = async <T = DocumentData>(
  cacheKey: string,
  firestoreQuery: Query,
  forceRefresh = false,
  ttl: number = 5 * 60 * 1000 // Default 5 mins for queries
): Promise<T[]> => {
  if (!forceRefresh) {
    const cached = dataCache.get<T[]>(cacheKey);
    if (cached) return cached;
  }

  if (pendingRequests[cacheKey]) {
    return pendingRequests[cacheKey];
  }

  const fetchPromise = (async () => {
    try {
      console.log(`Fetching query: ${cacheKey} from Firestore`);
      const snapshot = await withTimeout(getDocs(firestoreQuery), 8000);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as T));
      dataCache.set(cacheKey, data, ttl);
      return data;
    } catch (error: any) {
      const errMsg = error?.message || String(error);
      const isPermError = errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions');
      const isUnavailable = isUnavailableOrOfflineError(error);

      if (isQuotaError(error) || isPermError || isUnavailable || errMsg.includes('timed out')) {
        const fallback = dataCache.get<T[]>(cacheKey, true);
        if (fallback) {
          console.warn(`Quota, permission, timeout or connection issue for query ${cacheKey}, using fallback data.`);
          return fallback;
        }
        if (isPermError) {
          console.warn(`Permission denied for query ${cacheKey}, returning empty array.`);
          return [];
        }
        if (isUnavailable || errMsg.includes('timed out')) {
          console.warn(`Firestore backend unavailable/timed out for query ${cacheKey}, returning empty array.`);
          return [];
        }
        console.error(`Quota exceeded for query ${cacheKey} and no fallback available.`);
      }
      throw error;
    } finally {
      delete pendingRequests[cacheKey];
    }
  })();

  pendingRequests[cacheKey] = fetchPromise;
  return fetchPromise;
};

/**
 * Fetches a single document with caching and request deduplication.
 */
export const getCachedDoc = async <T = DocumentData>(
  collectionName: string,
  docId: string,
  forceRefresh = false,
  ttl: number = 5 * 60 * 1000
): Promise<T | null> => {
  const cacheKey = `doc_${collectionName}_${docId}`;
  if (!forceRefresh) {
    const cached = dataCache.get<T>(cacheKey);
    if (cached) return cached;
  }

  if (pendingRequests[cacheKey]) {
    return pendingRequests[cacheKey];
  }

  const fetchPromise = (async () => {
    try {
      console.log(`Fetching doc: ${collectionName}/${docId} from Firestore`);
      const snap = await withTimeout(getDoc(doc(db, collectionName, docId)), 8000);
      if (snap.exists()) {
        const data = snap.data() as T;
        dataCache.set(cacheKey, data, ttl);
        return data;
      }
      return null;
    } catch (error: any) {
      const errMsg = error?.message || String(error);
      const isPermError = errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions');
      const isUnavailable = isUnavailableOrOfflineError(error);

      if (isQuotaError(error) || isPermError || isUnavailable || errMsg.includes('timed out')) {
        const fallback = dataCache.get<T>(cacheKey, true);
        if (fallback) return fallback;
        console.warn(`Quota, permission, timeout or connection issue for doc ${collectionName}/${docId}, returning null.`);
        return null;
      }
      throw error;
    } finally {
      delete pendingRequests[cacheKey];
    }
  })();

  pendingRequests[cacheKey] = fetchPromise;
  return fetchPromise;
};

/**
 * Fetches students with caching.
 * When year is provided:
 * - Filters for students enrolled and active in that year.
 * - Resolves student's classId and className according to that year's enrollment.
 * When year is not provided:
 * - If includeInactive is true, returns all students including inactive.
 * - If includeInactive is false, returns all students where student.active !== false.
 */
export const getCachedStudents = async (forceRefresh = false, year?: string, includeInactive = false): Promise<any[]> => {
  const allStudents = await getCachedCollection('students', forceRefresh, 5 * 60 * 1000); // 5 mins
  if (includeInactive) {
    return allStudents;
  }
  if (!year) {
    return allStudents.filter((s: any) => s.active !== false && s.isActive !== false && s.status !== 'inactive');
  }

  return allStudents
    .filter((s: any) => {
      // Student must not be deleted or deactivated globally
      if (s.active === false || s.isActive === false || s.status === 'inactive') return false;
      // Check enrollment for the given year
      const enrollment = s.enrollments?.[year];
      if (enrollment) {
        if (enrollment.active === false || enrollment.isActive === false || enrollment.status === 'inactive') {
          return false;
        }
        return !!(enrollment.classId || enrollment.className);
      }
      // Legacy fallback for 2025-2026
      if (year === '2025-2026') {
        return s.active !== false && s.isActive !== false && s.status !== 'inactive' && !!(s.classId || s.className);
      }
      return false;
    })
    .map((s: any) => {
      const enrollment = s.enrollments?.[year];
      const effectiveTransport = enrollment?.transportName !== undefined ? enrollment.transportName : (s.transportName || '');
      const effectiveEscort = enrollment?.escortName !== undefined ? enrollment.escortName : (s.escortName || '');

      if (enrollment && (enrollment.classId || enrollment.className)) {
        return {
          ...s,
          classId: enrollment.classId || s.classId || '',
          className: enrollment.className || s.className || '',
          transportName: effectiveTransport,
          escortName: effectiveEscort,
          active: true
        };
      }
      return {
        ...s,
        transportName: effectiveTransport,
        escortName: effectiveEscort,
        active: true
      };
    });
};

/**
 * Fetches classes with caching.
 */
export const getCachedClasses = async (forceRefresh = false): Promise<any[]> => {
  return await getCachedCollection('classes', forceRefresh, 30 * 60 * 1000); // 30 mins
};

/**
 * Fetches staff with caching.
 */
export const getCachedStaff = async (forceRefresh = false): Promise<any[]> => {
  return await getCachedCollection('staff', forceRefresh, 5 * 60 * 1000); // 5 mins
};
